import type { HttpErrorResponse } from '@angular/common/http';
import type { EntriesLoadError } from './entries-load-error.types';

/**
 * Shared by the linked/linkage Formly fields that classify their permission-gated
 * `EntriesService.getEntries` failures: `linked-items`, `linkage-items`, `linkage-tags`,
 * `linkage-select` and `select-linked-entries`. (Other entry-fetching fields — e.g.
 * `required-items` — do not classify yet; a 403 there still surfaces as an empty field.)
 * A bare `catchError(() => of([]))` on that call renders a 403 the same way as "there are no
 * linked items" — the user cannot tell a missing permission from an empty result. Classify the
 * error first, then let the caller pick copy/behavior instead of swallowing it.
 *
 * Data-cy convention for the notice that renders the classified error:
 * `<field-prefix>_load-error` (e.g. `linked-items_load-error`), with a retry action — where the
 * field offers one — at `<field-prefix>_retry-button`.
 */
export function classifyEntriesLoadError(error: HttpErrorResponse): EntriesLoadError {
  return error.status === 403 ? 'forbidden' : 'failed';
}

/**
 * Dutch copy for a load-error notice. `label` is the field's `props.label` as configured
 * (unlowercased) — the 'forbidden' copy lowercases it itself so it reads naturally mid-sentence.
 * Formly `props` is untyped, so a label-less field config reaches this as `undefined` at runtime
 * even without strictTemplates catching it at compile time — falls back to 'gekoppelde items'.
 */
export function entriesLoadErrorMessage(error: EntriesLoadError, label: string | undefined): string {
  const resolvedLabel = label ?? 'gekoppelde items';
  return error === 'forbidden'
    ? `Je hebt geen toegang tot ${resolvedLabel.toLowerCase()}.`
    : `${resolvedLabel} konden niet worden geladen. Probeer het later opnieuw.`;
}
